import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { IconButton, Tooltip } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { ScreenComponent } from 'new/routes/RouterManager';
import { ROUTE_REDESIGN } from 'js/constants/pathnames';
import { Stack } from 'new/elements/Stack';
import {
  useTriageLauncher,
  buildTriageContextFromResource,
} from 'new/components/monitor/TriageChat';
import { Tabs } from 'new/elements/Tabs';
import {
  fetchAuditLog,
  fetchDataMapDoc,
  fetchDatasetDoc,
  fetchPipelineDoc,
  fetchResourceCronHistory,
  fetchResourceFileStats,
  fetchResourceNotificationsPaginated,
  fetchSchemaSpecDoc,
  fetchTransformDoc,
  getCodeContainer,
  getDataCredential,
  getSchemaSpec,
  loadDataMap,
  loadDataSet,
  loadDataSink,
  loadDataSource,
  loadFullVendor,
  loadPipeline,
  removeDetailViewCard,
} from 'js/actions';
import { Resume } from 'new/components/monitor/ResourceTriage/components/Resume';
import {
  RESOURCE_TYPE_CREDENTIALS,
  RESOURCE_TYPE_DATA_MAP,
  RESOURCE_TYPE_DATA_SET,
  RESOURCE_TYPE_FLOW,
  RESOURCE_TYPE_RECORD_TRANSFORM,
  RESOURCE_TYPE_SCHEMA_SPECS,
  RESOURCE_TYPE_SINK,
  RESOURCE_TYPE_SOURCE,
} from 'js/constants/resourceTypes';
import { Activity } from 'new/elements/Activity';
import { useResource } from 'new/components/monitor/ResourceTriage/useResource';
import { CenteredLoader } from 'new/elements/CenteredLoader';
import { Notifications } from 'new/elements/Notifications';
import { ReadStats } from 'new/elements/ReadStats';
import { useResourceMeta } from 'new/hooks/adapters/useResourceMeta';
import { isSqlAutomation } from 'js/lib/ResourceUtils';
import {
  CONNECTION_CATEGORY_DATA_MAP,
  CONNECTION_CATEGORY_DATABASE,
  CONNECTION_CATEGORY_FILE,
  CONNECTION_CATEGORY_REST,
  CONNECTION_CATEGORY_TEMPLATIZED_EMAIL_SCRIPT,
  TIME_BASED_METRICS_FILE_CONNECTORS,
} from 'js/constants/connectionCategories';
import { ModalReingest } from 'new/elements/ModalReingest';
import { ModalFlowPreview } from 'new/elements/ModalFlowPreview';
import { ResourceSearch } from 'new/elements/ResourceSearch';
import { ErrorData } from 'new/elements/ErrorData';
import { FILE_LIST_MODE_TYPES } from 'js/constants/paginationConstants';
import {
  ResourceTriageContext,
  ResourceTriageContextProvider,
} from 'new/components/monitor/ResourceTriage/ResourceTriageContext';
import { Stats } from 'new/elements/Stats';
import { Overview } from 'new/elements/Overview';
import { ACLQueryModeTypes } from 'js/constants/visibilityFilters';
import { ModalFilePreview } from 'new/elements/ModalFilePreview';
import { ModalMoveToProject } from 'new/elements/ModalMoveToProject';
import { BannerDescription } from 'new/elements/BannerDescription';
import NoContentTextView from 'js/components/elements/NoContentTextView';
import FlowInsights from 'js/components/data_flows/cards/FlowInsights';
import FlowDetailViewContainer from 'js/components/data_flows/cards/FlowDetailViewContainer';
import useFileItems from 'new/elements/ReadStats/hooks/useFileItems';
import { notificationTypes } from 'new/components/monitor/ResourceTriage/consts';
import { LinkedFlows } from 'js/components/elements/LinkedFlows';
import useAppDispatch from 'js/hooks/useAppDispatch';
import DataSetDataBrowserContainer from 'js/components/data_sets/cards/DataSetDataBrowserContainer';
import DataSetRecipeTab from 'js/components/data_sets/cards/DataSetRecipeTab';
import { EmptyState } from 'new/elements/EmptyState/EmptyState';
import { CredentialUsage } from 'new/components/DetailViewTabs/Credential/components/CredentialUsage/CredentialUsage';
import { getAllowedNexsetTabs, NEXSET_TAB_IDS, TAB_METADATA } from 'new/utils/nexsetTabs';

import styles from './ResourceTriage.module.scss';

interface ResourceTriageProps {}

interface TopProps {
  loading: boolean;
}
const description = 'View Resource events to assess issue severity.';

const Top = ({ loading }: TopProps) => {
  const { type, id, onResourceChange } = useContext(ResourceTriageContext);
  const { resource } = useResource(type, id);
  const triage = useTriageLauncher();

  const onSearch = (data: { type: string; id: string }) => {
    if (onResourceChange) {
      onResourceChange(data.type, data.id);
    }
  };

  return (
    <div className={styles.container}>
      <BannerDescription title="Resource Triage" description={description} />
      <Stack className={styles.top}>
        <Stack className={styles.topContent}>
          <Stack direction="row" alignItems="flex-start" gap="8px">
            <ResourceSearch ignoreErrors noRequest onSearch={onSearch} type={type} id={id} />
            {triage.enabled && !!id && (
              <Tooltip title="Triage with AI">
                {/* ResourceSearch stacks a 24px label above its inputs and the
                   "Find Resource" button is offset margin-top:24px to sit on the
                   input row; match that offset so the icon lines up with it. */}
                <span style={{ marginTop: 'var(--spacing-xl)' }}>
                  <IconButton
                    color="primary"
                    size="small"
                    disabled={loading}
                    data-testid="triage-resource-page-button"
                    aria-label="Triage with AI"
                    onClick={() =>
                      triage.open(
                        buildTriageContextFromResource({
                          resourceType: type,
                          resourceId: id,
                          resourceName: resource?.name,
                        }),
                        'resource_triage_page',
                      )
                    }
                  >
                    <AutoAwesomeIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Stack>
          {loading ? <CenteredLoader absolute /> : null}
        </Stack>
      </Stack>
    </div>
  );
};

// tab configuration mapping for each resource type
const RESOURCE_TAB_CONFIGS = {
  [RESOURCE_TYPE_SOURCE]: (
    connectionCategory: string,
    resource: any,
    vendors: Record<string, any> = {},
  ) => {
    const sqlAutomationTabs = [
      { value: 'overview', label: 'Overview', Component: Overview },
      { value: 'activity', label: 'Activity', Component: Activity },
      {
        value: 'notifications',
        label: 'Notifications',
        Component: Notifications,
      },
    ];

    if (resource?.source_type === 'api_web_server') {
      return sqlAutomationTabs;
    }

    if (isSqlAutomation(resource, vendors)) {
      return sqlAutomationTabs;
    }

    return [
      { value: 'overview', label: 'Overview', Component: Overview },
      { value: 'readStats', label: 'Read stats', Component: ReadStats },
      { value: 'activity', label: 'Activity', Component: Activity },
      { value: 'errors', label: 'Error data', Component: ErrorData },
      {
        value: 'notifications',
        label: 'Notifications',
        Component: Notifications,
      },
    ];
  },

  [RESOURCE_TYPE_SINK]: (connectionCategory: string, resource: any) => {
    const tabs = [
      { value: 'overview', label: 'Overview', Component: Overview },
      { value: 'activity', label: 'Activity', Component: Activity },
      { value: 'errors', label: 'Error data', Component: ErrorData },
      {
        value: 'notifications',
        label: 'Notifications',
        Component: Notifications,
      },
    ];

    // add write stats for specific connection categories
    if (
      [
        CONNECTION_CATEGORY_FILE,
        CONNECTION_CATEGORY_REST,
        CONNECTION_CATEGORY_DATABASE,
        CONNECTION_CATEGORY_DATA_MAP,
      ].includes(connectionCategory)
    ) {
      tabs.splice(1, 0, {
        value: 'readStats',
        label: 'Write stats',
        Component: ReadStats,
      });
    }

    return tabs;
  },

  [RESOURCE_TYPE_DATA_SET]: (connectionCategory: string, resource: any) => {
    const componentMap: Record<string, React.ComponentType<any>> = {
      [NEXSET_TAB_IDS.OVERVIEW]: Overview,
      [NEXSET_TAB_IDS.SCHEMA]: DataSetDataBrowserContainer,
      [NEXSET_TAB_IDS.RULES]: DataSetRecipeTab,
      [NEXSET_TAB_IDS.STATS]: Stats,
      [NEXSET_TAB_IDS.ACTIVITY]: Activity,
      [NEXSET_TAB_IDS.ERROR_DATA]: ErrorData,
      [NEXSET_TAB_IDS.NOTIFICATIONS]: Notifications,
    };

    const allowedTabIds = getAllowedNexsetTabs({
      data: resource,
      flowType: resource?.flow_type,
      nodeType: resource?.node_type,
      connectionCategory,
    });

    // ResourceTriage doesn't show Share tab, so filter it out
    const filteredTabIds = allowedTabIds.filter((tabId) => tabId !== NEXSET_TAB_IDS.SHARE);

    return filteredTabIds.map((tabId) => {
      const metadata = TAB_METADATA[tabId];
      const Component = componentMap[tabId];

      return {
        value: tabId,
        label: metadata.label,
        Component,
      };
    });
  },

  [RESOURCE_TYPE_FLOW]: (connectionCategory: string, resource: any) => {
    const tabs = [
      { value: 'overview', label: 'Overview', Component: Overview },
      { value: 'insights', label: 'Insights', Component: FlowInsights },
      { value: 'linked', label: 'Linked Flows', Component: LinkedFlows },
    ];

    // remove linked flows for RAG flow types
    if (resource?.flow_type === 'rag') {
      tabs.pop();
    }

    return tabs;
  },

  [RESOURCE_TYPE_SCHEMA_SPECS]: (connectionCategory: string, resource: any) => [
    { value: 'overview', label: 'Overview', Component: Overview },
    { value: 'activity', label: 'Activity', Component: Activity },
  ],

  [RESOURCE_TYPE_RECORD_TRANSFORM]: (connectionCategory: string, resource: any) => [
    { value: 'overview', label: 'Overview', Component: Overview },
    { value: 'activity', label: 'Activity', Component: Activity },
  ],

  [RESOURCE_TYPE_CREDENTIALS]: (connectionCategory: string, resource: any) => [
    { value: 'overview', label: 'Overview', Component: Overview },
    { value: 'usage', label: 'Usage', Component: CredentialUsage },
  ],

  [RESOURCE_TYPE_DATA_MAP]: (connectionCategory: string, resource: any) => [
    { value: 'overview', label: 'Overview', Component: Overview },
  ],
};

const getTabs = (
  resourceType: string,
  connectionCategory: string,
  resource: any,
  vendors: Record<string, any> = {},
) => {
  const tabConfig = RESOURCE_TAB_CONFIGS[resourceType];
  if (!tabConfig) {
    // Fallback to basic tabs if resource type not found
    return [
      { value: 'overview', label: 'Overview', Component: Overview },
      { value: 'activity', label: 'Activity', Component: Activity },
    ];
  }

  return tabConfig(connectionCategory, resource, vendors);
};

const initialFilters = {
  [RESOURCE_TYPE_SINK]: {
    page: 1,
    size: 100,
    orderby: 'lastWritten&sortorder=desc',
    groupby: 'lastWritten',
  },
  [RESOURCE_TYPE_SOURCE]: {
    page: 1,
    size: 100,
    orderby: 'runId&sortorder=desc',
  },
};

export const resourceFetchActions = {
  [RESOURCE_TYPE_SINK]: loadDataSink,
  [RESOURCE_TYPE_SOURCE]: loadDataSource,
  [RESOURCE_TYPE_DATA_SET]: loadDataSet,
  [RESOURCE_TYPE_CREDENTIALS]: getDataCredential,
  [RESOURCE_TYPE_DATA_MAP]: loadDataMap,
  [RESOURCE_TYPE_FLOW]: loadPipeline,
  [RESOURCE_TYPE_SCHEMA_SPECS]: getSchemaSpec,
  [RESOURCE_TYPE_RECORD_TRANSFORM]: getCodeContainer,
};

export const resourceDocFetchActions = {
  [RESOURCE_TYPE_DATA_SET]: fetchDatasetDoc,
  [RESOURCE_TYPE_RECORD_TRANSFORM]: fetchTransformDoc,
  [RESOURCE_TYPE_SCHEMA_SPECS]: fetchSchemaSpecDoc,
  [RESOURCE_TYPE_DATA_MAP]: fetchDataMapDoc,
  [RESOURCE_TYPE_FLOW]: fetchPipelineDoc,
};

export const ResourceTriage: ScreenComponent<ResourceTriageProps> = (
  props: ResourceTriageProps,
) => {
  return (
    <ResourceTriageContextProvider>
      <ResourceTriageComponent {...props} />
    </ResourceTriageContextProvider>
  );
};

const ResourceTriageComponent = () => {
  const [triedToLoad, setTriedToLoad] = useState(false);
  const dispatch = useAppDispatch();
  const selectedCardID = useSelector(
    (state: { pipelines: { detailView: { nodeKey: number | string | null } } }) =>
      state.pipelines.detailView.nodeKey,
  );

  useEffect(() => {
    dispatch(removeDetailViewCard());
    return () => {
      dispatch(removeDetailViewCard());
    };
  }, [dispatch]);
  const {
    tab: initialTabFromParams,
    type: resourceType,
    id: resourceId,
    onResourceChange,
  } = useContext(ResourceTriageContext);
  const { resource, loading } = useResource(resourceType, resourceId);
  const meta = useResourceMeta(resourceType, resourceId);
  const vendors = useSelector((state: any) => state.entities?.vendors ?? {});
  const tabs = getTabs(resourceType, meta?.connectionCategory, resource, vendors);
  const [selectedTab, setTab] = useState(tabs[0]);

  let useFiles = false;
  if (
    [CONNECTION_CATEGORY_FILE, CONNECTION_CATEGORY_TEMPLATIZED_EMAIL_SCRIPT].includes(
      meta?.connectionCategory,
    ) &&
    !TIME_BASED_METRICS_FILE_CONNECTORS.includes(meta?.key)
  ) {
    useFiles = true;
  }
  useFileItems(resourceId, resourceType, useFiles);

  const onTabChange = (e, value) => {
    setTab(tabs.find((tab) => tab.value === value));
  };

  const loadAudit = () => {
    if (![RESOURCE_TYPE_FLOW, RESOURCE_TYPE_RECORD_TRANSFORM].includes(resourceType)) {
      dispatch(fetchAuditLog(resourceType, resourceId));
    }
  };

  const loadFileStats = () => {
    if ([RESOURCE_TYPE_SOURCE, RESOURCE_TYPE_SINK].includes(resourceType)) {
      if (useFiles) {
        dispatch(fetchResourceFileStats(resourceId, resourceType));
      } else {
        dispatch(fetchResourceCronHistory(resourceId, resourceType, initialFilters[resourceType]));
      }
    }
  };

  const loadResource = async () => {
    const action = resourceFetchActions[resourceType];
    if (action) {
      try {
        const response = await dispatch(
          action(resourceId, {
            aclQueryMode: ACLQueryModeTypes.MODE_COLLABORATOR.key,
            ignoreErrors: true,
            expand: true,
          }),
        );
        const vendorId = Object.values(
          response?.entities?.credentials || {},
          // @ts-ignore
        )?.[0]?.vendor_id;
        const vendor = response?.entities?.vendors?.[vendorId];

        if (vendor && vendor?.name) {
          dispatch(loadFullVendor(vendor.name));
        }
      } catch (e) {}
    }
  };

  const loadNotifications = () => {
    const notificationType = notificationTypes[resourceType];
    if (notificationType) {
      dispatch(
        fetchResourceNotificationsPaginated(resourceId, notificationType, {
          page: 1,
          per_page: 100,
        }),
      );
    }
  };

  const loadDoc = () => {
    if (Object.keys(resourceDocFetchActions).includes(resourceType)) {
      const action = resourceDocFetchActions[resourceType];
      dispatch(action(resourceId));
    }
  };

  const loadData = () => {
    loadResource();
    loadAudit();
    loadFileStats();
    loadNotifications();
    loadDoc();
  };

  useEffect(() => {
    if (resourceId) {
      loadData();
      setTriedToLoad(true);

      const preSelectedTab = initialTabFromParams
        ? tabs.find((tab) => tab.value === initialTabFromParams)
        : undefined;

      setTab(preSelectedTab || tabs[0]);
    }
  }, [resourceId, resourceType, useFiles]);

  // Per-node flow metrics for the FLOW Overview DAG are driven by the Overview
  // itself (FlowOverview → useFlowMetricsSync), which fetches on mount and honors
  // the interval toggle. Doing it here too would double-fetch.

  const onItemClick = useCallback((type, data) => {
    onResourceChange(type, data.id);
  }, []);

  const Component = resource ? selectedTab?.Component || React.Fragment : React.Fragment;

  const renderEmptyState = () => {
    // if the user has not tried to load the resource
    if (!triedToLoad) {
      return (
        <EmptyState
          title="Find a Resource to Begin"
          description="Select a Resource Type and enter a Resource ID to view event details and identify issues."
          tip={
            <>
              Need help finding a Resource ID?{' '}
              <Link to="/integrate/flows" target="_blank">
                Check your Data Flows
              </Link>{' '}
              (opens in new tab) or ask your admin.
            </>
          }
        />
      );
    }

    // if user has tried to load the resource and the resource is not loaded, show the empty state
    // if the resource is not loaded and the loading is false, show the empty state
    if (triedToLoad || !resource || !loading) {
      return (
        <NoContentTextView
          title={<div>Please select a Resource Type and enter a valid Resource ID</div>}
        />
      );
    }
  };

  return (
    <Stack height="100%" className={styles.resourceTriageContainer}>
      <Top loading={loading} />
      <Stack direction="row" className={styles.resource}>
        <Stack className={styles.content}>
          {!resource && !loading && renderEmptyState()}
          {!!resource ? (
            <>
              <Stack flex={0} className={styles.tabs}>
                <Tabs
                  value={selectedTab?.value}
                  tabs={tabs}
                  defaultValue="activity"
                  onChange={onTabChange}
                />
              </Stack>
              <Stack className={styles.tabContent}>
                <Component
                  data={resource}
                  // @ts-ignore
                  connectionCategory={meta?.connectionCategory}
                  // @ts-ignore
                  mode={
                    resourceType === RESOURCE_TYPE_SOURCE
                      ? FILE_LIST_MODE_TYPES.MODE_READ
                      : FILE_LIST_MODE_TYPES.MODE_WRITE
                  }
                  // @ts-ignore
                  useFiles={useFiles}
                  triedLoad={triedToLoad}
                  resourceType={resourceType}
                  notificationType={notificationTypes[resourceType]}
                  resourceId={resourceId}
                  credentialId={resourceId}
                  onItemClick={onItemClick}
                />
              </Stack>
            </>
          ) : null}
        </Stack>
        <Stack width="280px" minWidth="280px">
          {!!resource || loading ? (
            <Resume
              {...resource}
              rootClassName={styles.rootResume}
              loading={loading}
              resourceType={resourceType}
              id={resourceId}
              onFlowItemClicked={onItemClick}
            />
          ) : null}
        </Stack>
      </Stack>
      {selectedCardID && <FlowDetailViewContainer />}
    </Stack>
  );
};

ResourceTriage.screenConfig = {
  path: `${ROUTE_REDESIGN}/monitor/resource-triage`,
  domain: 'monitor',
  modals: [ModalReingest, ModalFlowPreview, ModalFilePreview, ModalMoveToProject],
  header: {
    title: 'Resource Triage',
    slugTitle: 'Resource Triage',
  },
  screenName: 'ResourceTriage',
};
